import { useMemo, useRef, useState } from 'react'

import { Trigger } from '@radix-ui/react-select'

import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Select, SelectContent, SelectItem } from '@/components/ui/select'
import { TextField } from '@/components/ui/text-field'

import { DEFAULT_COUNTRY, filteredCountries } from './consts'
import {
  applyPhoneNumberMask,
  findCountryId,
  findDialCode,
  formatCountryCode,
  getPhoneNumberMask,
} from './utils'

import 'react-intl-tel-input/dist/main.css'

interface Props {
  value?: string
  onChange: (value: string) => void
}

export const PhoneNumberInput = ({ value, onChange }: Props) => {
  const [query, setQuery] = useState('')
  const [countryCode, setCountryCode] = useState(
    findCountryId(value || '') || DEFAULT_COUNTRY,
  )
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  const countryList = useMemo(() => {
    return Object.values(filteredCountries).filter((country) =>
      country.name.toLowerCase().includes(query.toLowerCase()),
    )
  }, [query])

  const handleInputChange = (value: string) => {
    onChange(applyPhoneNumberMask(value, getPhoneNumberMask(value)))
  }

  const onChangePhoneNumber: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    handleInputChange(event.target.value.replace(' ', ''))
  }

  const onChangeCountryCode = (countryCode: string) => {
    const formattedCountryCode = formatCountryCode(countryCode)

    setCountryCode(formattedCountryCode)
    const dialCode = findDialCode(formattedCountryCode)

    if (dialCode) {
      handleInputChange('+' + dialCode)
    }
  }

  return (
    <TextField
      showPrefixSeparator
      onChange={onChangePhoneNumber}
      value={value}
      prefix={
        <Select onValueChange={onChangeCountryCode}>
          <Trigger className="group flex items-center gap-2 outline-none">
            <span
              className={`iti-flag ${countryCode.toLowerCase()} inline-block h-4 w-5`}
            />
            <ExpandMoreIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
          </Trigger>
          <SelectContent
            className="max-h-[406px] max-w-full relative pt-0 min-w-[300px]"
            viewPortClassName="mt-[42px]"
            sideOffset={20}
            alignOffset={-12}
            header={
              <div className="fixed z-10 top-0 right-0 left-0">
                <TextField
                  autoFocus
                  type="text"
                  placeholder="Search for Country"
                  showPrefixSeparator
                  onChange={handleSearchChange}
                  ref={searchInputRef}
                  value={query}
                  prefix={
                    <SearchIcon className="text-secondary-500 dark:text-secondary-300" />
                  }
                  className="border-none text-sm"
                />
                <div className="border-b" />
              </div>
            }
          >
            {countryList.length ? (
              countryList.map((country) => (
                <SelectItem
                  key={country.id}
                  value={country.id}
                >
                  <span
                    className={`iti-flag ${country.id} inline-block h-4 w-5`}
                  />{' '}
                  {country.name} (+{country.dialCode})
                </SelectItem>
              ))
            ) : (
              <div className="text-center py-1 text-sm">No countries found</div>
            )}
          </SelectContent>
        </Select>
      }
    />
  )
}
